import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, TrendingUp, Target, CheckCircle2, Headphones, Lock, DollarSign, PackageCheck, BadgeCheck, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useSettings } from "@/context/SettingsContext";
import ServiceCard from "@/components/ServiceCard";
import { SectionHeading, Reveal } from "@/components/Section";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import logo from "@/assets/adnexa-logo.png";

export default function Home() {
  const { t } = useI18n();
  const { settings } = useSettings();
  const [services, setServices] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [posts, setPosts] = useState([]);
  const sec = settings.sections || {};

  useEffect(() => {
    api.get("/services", { params: { featured: true } }).then((r) => setServices(r.data.slice(0, 6))).catch(() => {});
    api.get("/faq", { params: { location: "homepage" } }).then((r) => setFaqs(r.data)).catch(() => {});
    api.get("/blog").then((r) => setPosts(r.data.slice(0, 3))).catch(() => {});
  }, []);

  const why = [
    { icon: Target, t: "why.strategic", d: "why.strategicD" },
    { icon: ShieldCheck, t: "why.transparent", d: "why.transparentD" },
    { icon: Headphones, t: "why.support", d: "why.supportD" },
    { icon: TrendingUp, t: "why.performance", d: "why.performanceD" },
  ];
  const trust = [
    { icon: Lock, k: "trust.secure" }, { icon: DollarSign, k: "trust.pricing" },
    { icon: Headphones, k: "trust.support" }, { icon: PackageCheck, k: "trust.tracking" },
    { icon: BadgeCheck, k: "trust.verified" },
  ];
  const how = settings.how_it_works || [];

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden grid-glow" data-testid="hero-section">
        <div className="absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full bg-amber-500/10 blur-[120px]" />
        <div className="absolute top-1/2 -left-40 h-[400px] w-[400px] rounded-full bg-amber-600/5 blur-[120px]" />
        <div className="container-lux relative pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-2 gap-14 items-center">
          <div className="fade-up">
            <div className="inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/5 px-4 py-1.5 mb-6">
              <Sparkles size={14} className="text-amber-400" />
              <span className="text-xs font-semibold tracking-wide text-amber-300">{t("hero.badge")}</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-white">
              {(settings.hero_title || "Scale Your Digital Growth With ADNEXA DIGITAL")
                .split(/(ADNEXA DIGITAL)/i)
                .map((part, i) => /ADNEXA DIGITAL/i.test(part)
                  ? <span key={i} className="gold-text">{part}</span>
                  : <span key={i}>{part}</span>)}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-400 max-w-xl leading-relaxed">
              {settings.hero_subtitle || "Performance marketing, paid advertising and SEO solutions built to help businesses reach the right audience and grow online."}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/services" className="gold-btn" data-testid="hero-explore-button">{t("hero.cta1")} <ArrowRight size={17} /></Link>
              <Link to="/register" className="silver-btn" data-testid="hero-getstarted-button">{t("hero.cta2")}</Link>
              <Link to="/contact" className="silver-btn" data-testid="hero-contact-button">{t("hero.cta3")}</Link>
            </div>
            <p className="mt-10 text-xs sm:text-sm font-medium text-slate-500 tracking-wide" data-testid="hero-trustline">{t("hero.trustline")}</p>
          </div>
          <div className="relative fade-up" style={{ animationDelay: "150ms" }}>
            <div className="relative rounded-3xl border border-amber-500/20 bg-gradient-to-br from-[#14161c] to-[#0b0c0e] p-8 sm:p-12 shadow-2xl">
              <div className="absolute inset-0 rounded-3xl grid-glow opacity-40" />
              <img src={logo} alt="ADNEXA DIGITAL" className="relative w-full max-w-md mx-auto animate-floaty drop-shadow-[0_0_40px_rgba(212,175,55,0.25)]" />
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      {sec.services !== false && (
        <section className="section-lux" data-testid="home-services">
          <div className="container-lux">
            <SectionHeading label="Our Services" title={t("home.servicesTitle")} subtitle={t("home.servicesSub")} />
            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((s, i) => <ServiceCard key={s.id} service={s} index={i} />)}
            </div>
            <div className="mt-10 text-center">
              <Link to="/services" className="silver-btn" data-testid="home-all-services">{t("common.viewAll")} <ArrowRight size={16} /></Link>
            </div>
          </div>
        </section>
      )}

      {/* WHY */}
      {sec.why !== false && (
        <section className="section-lux bg-[#0e1013] border-y border-amber-500/10" data-testid="home-why">
          <div className="container-lux">
            <SectionHeading label="Why ADNEXA" title={t("home.whyTitle")} subtitle={t("home.whySub")} />
            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {why.map((w, i) => (
                <Reveal key={i} delay={i * 70} className="card-lux p-6">
                  <w.icon className="text-amber-400 mb-4" size={26} />
                  <h3 className="font-semibold text-white mb-2">{t(w.t)}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{t(w.d)}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* HOW */}
      {sec.how !== false && how.length > 0 && (
        <section className="section-lux" data-testid="home-how">
          <div className="container-lux">
            <SectionHeading label="Process" title={t("home.howTitle")} subtitle={t("home.howSub")} />
            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {how.map((s, i) => (
                <Reveal key={i} delay={i * 80} className="relative card-lux p-6">
                  <div className="font-display text-5xl font-extrabold gold-text opacity-80">{s.step}</div>
                  <h3 className="font-semibold text-white mt-3 mb-2">{s.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{s.desc}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CONFIDENCE */}
      <section className="section-lux" data-testid="home-confidence">
        <div className="container-lux">
          <SectionHeading label="How Ordering Works" title={t("home.confTitle")} subtitle={t("home.confSub")} />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {["conf.s1", "conf.s2", "conf.s3", "conf.s4", "conf.s5"].map((k, i) => (
              <Reveal key={k} delay={i * 60} className="card-lux p-6 text-center">
                <div className="mx-auto h-11 w-11 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mb-4">
                  <CheckCircle2 className="text-amber-400" size={19} />
                </div>
                <div className="text-xs text-slate-600 font-mono mb-1">0{i + 1}</div>
                <div className="text-sm font-medium text-white">{t(k)}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="py-14 border-y border-amber-500/10 bg-[#0e1013]" data-testid="home-trust">
        <div className="container-lux">
          <p className="text-center label-lux mb-8">{t("home.trustTitle")}</p>
          <div className="flex flex-wrap justify-center gap-x-10 gap-y-5">
            {trust.map((x, i) => (
              <div key={i} className="flex items-center gap-2.5 text-slate-300">
                <x.icon size={18} className="text-amber-400" />
                <span className="text-sm font-medium">{t(x.k)}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      {sec.faq !== false && faqs.length > 0 && (
        <section className="section-lux" data-testid="home-faq">
          <div className="container-lux max-w-3xl">
            <SectionHeading label="FAQ" title={t("home.faqTitle")} />
            <Accordion type="single" collapsible className="mt-10">
              {faqs.map((f) => (
                <AccordionItem key={f.id} value={f.id} className="card-lux mb-3 px-5 border-none">
                  <AccordionTrigger className="text-left text-white hover:no-underline" data-testid={`home-faq-${f.id}`}>{f.question}</AccordionTrigger>
                  <AccordionContent className="text-slate-400">{f.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      {/* BLOG */}
      {sec.blog !== false && posts.length > 0 && (
        <section className="section-lux bg-[#0e1013] border-y border-amber-500/10" data-testid="home-blog">
          <div className="container-lux">
            <SectionHeading label="Insights" title={t("home.blogTitle")} />
            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((p, i) => (
                <Link key={p.id} to={`/blog/${p.slug}`} className="card-lux overflow-hidden fade-up" style={{ animationDelay: `${i * 70}ms` }} data-testid={`home-blog-${p.slug}`}>
                  {p.featured_image && <img src={p.featured_image} alt={p.title} className="h-44 w-full object-cover" />}
                  <div className="p-5">
                    <div className="label-lux mb-2">{p.category}</div>
                    <h3 className="font-semibold text-white line-clamp-2">{p.title}</h3>
                    <p className="text-sm text-slate-400 mt-2 line-clamp-2">{p.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      {sec.cta !== false && (
        <section className="section-lux" data-testid="home-cta">
          <div className="container-lux">
            <div className="relative overflow-hidden rounded-3xl border border-amber-500/25 bg-gradient-to-br from-[#16130a] via-[#121418] to-[#0b0c0e] p-10 sm:p-16 text-center">
              <div className="absolute -top-20 left-1/2 -translate-x-1/2 h-64 w-64 rounded-full bg-amber-500/15 blur-[100px]" />
              <h2 className="relative text-3xl sm:text-4xl font-bold text-white">{t("home.ctaTitle")}</h2>
              <p className="relative mt-4 text-slate-400 max-w-xl mx-auto">{t("home.ctaSub")}</p>
              <div className="relative mt-8 flex flex-wrap justify-center gap-3">
                <Link to="/register" className="gold-btn" data-testid="cta-register-button">{t("hero.cta2")} <ArrowRight size={17} /></Link>
                <Link to="/services" className="silver-btn" data-testid="cta-services-button">{t("hero.cta1")}</Link>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
